import { Link } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsBuilding, BsChevronRight, BsFileText, BsFlag, BsMailbox, BsMap, BsTrophy, BsX } from 'react-icons/bs';

import SectionCard from '#globalComponents/Card/SectionCard';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

const STEPS = [
	{
		id: 'lead-search',
		icon: BsBuilding,
		title: 'Erste Lead-Suche starten',
		description: 'Finden Sie Unternehmen mit unseren präzisen Filtern',
		link: '/leads',
	},
	{
		id: 'map-search',
		icon: BsMap,
		title: 'Kartensuche erkunden',
		description: 'Entdecken Sie Unternehmen in Ihrer Nähe auf der Karte',
		link: '/map-search',
	},
	{
		id: 'email-list',
		icon: BsFileText,
		title: 'Kontaktliste erstellen',
		description: 'Organisieren Sie Ihre Kontakte für gezielte Kampagnen',
		link: '/contact-lists',
	},
	{
		id: 'email-campaign',
		icon: BsMailbox,
		title: 'Erste Kampagne starten',
		description: 'Erreichen Sie Ihre Zielgruppe mit E-Mail Kampagnen',
		link: '/email-campaigns',
	},
];

const StepItem = ({ step, isCompleted, onToggle }) => {
	const StepIcon = step.icon;
	const checkboxId = `getting-started-${step.id}`;

	return (
		<li
			className={`flex items-center gap-3 rounded-card border p-3 transition-colors ${
				isCompleted
					? 'border-success-500 bg-success-200'
					: 'border-bgBase-500 bg-white hover:border-primary-300'
			}`}
		>
			<Checkbox
				id={checkboxId}
				checked={isCompleted}
				onChange={onToggle}
				aria-label={isCompleted ? 'Als unerledigt markieren' : 'Als erledigt markieren'}
			/>
			<Link to={step.link} className="group flex flex-1 min-w-0 items-center gap-3">
				<span
					className={`h-9 w-9 rounded-control flex items-center justify-center flex-shrink-0 ${
						isCompleted ? 'bg-white text-success-700' : 'bg-primary-100 text-primary-600'
					}`}
				>
					<StepIcon className="w-4 h-4" />
				</span>
				<span className="flex-1 min-w-0">
					<span
						className={`block text-sm font-semibold ${
							isCompleted ? 'text-success-700 line-through' : 'text-text group-hover:text-primary-600'
						}`}
					>
						{step.title}
					</span>
					<span className="block text-xs text-bgBase-800">{step.description}</span>
				</span>
				<BsChevronRight className="w-3 h-3 text-bgBase-700 group-hover:text-primary-500 transition-colors" />
			</Link>
		</li>
	);
};

StepItem.propTypes = {
	step: PropTypes.shape({
		id: PropTypes.string.isRequired,
		icon: PropTypes.elementType.isRequired,
		title: PropTypes.string.isRequired,
		description: PropTypes.string.isRequired,
		link: PropTypes.string.isRequired,
	}).isRequired,
	isCompleted: PropTypes.bool.isRequired,
	onToggle: PropTypes.func.isRequired,
};

const GettingStartedCard = () => {
	const progress = usePreferencesStore((s) => s.dashboardProgress);
	const setProgress = usePreferencesStore((s) => s.setDashboardProgress);
	const completedSteps = progress.completedSteps ?? [];

	if (progress.isVisible === false) return null;

	const toggleStep = (stepId) =>
		setProgress({
			...progress,
			completedSteps: completedSteps.includes(stepId)
				? completedSteps.filter((id) => id !== stepId)
				: [...completedSteps, stepId],
		});

	const progressPct = (completedSteps.length / STEPS.length) * 100;
	const allStepsCompleted = completedSteps.length === STEPS.length;

	return (
		<SectionCard
			title="Erste Schritte"
			subtitle={`${completedSteps.length} von ${STEPS.length} erledigt – haken Sie Schritte ab, sobald Sie sie durchgeführt haben`}
			icon={BsFlag}
			headerAction={
				<button
					type="button"
					onClick={() => setProgress({ ...progress, isVisible: false })}
					className="h-8 w-8 flex items-center justify-center rounded-control text-bgBase-800 hover:bg-bgBase-400 hover:text-text transition-colors flex-shrink-0"
					aria-label="Erste Schritte ausblenden"
					title="Ausblenden"
				>
					<BsX className="w-5 h-5" />
				</button>
			}
		>
			<div className="h-1.5 rounded-full bg-bgBase-400 overflow-hidden mb-4">
				<div
					className={`h-full rounded-full transition-all duration-500 ${
						allStepsCompleted ? 'bg-success-500' : 'bg-primary-500'
					}`}
					style={{ width: `${progressPct}%` }}
				/>
			</div>

			{allStepsCompleted && (
				<p className="flex items-center gap-1.5 text-sm font-semibold text-success-700 mb-4">
					<BsTrophy className="w-4 h-4" />
					Glückwunsch! Alle Schritte abgeschlossen
				</p>
			)}

			<ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
				{STEPS.map((step) => (
					<StepItem
						key={step.id}
						step={step}
						isCompleted={completedSteps.includes(step.id)}
						onToggle={() => toggleStep(step.id)}
					/>
				))}
			</ul>
		</SectionCard>
	);
};

export default GettingStartedCard;
